import { useQueryClient } from "@tanstack/react-query";
import { RelativePathString, useGlobalSearchParams } from "expo-router";
import { type ReactNode, useContext, useEffect, useRef } from "react";

import { getOrCreateUserProgression } from "@/api/generated";
import {
  AffiliateOutcome,
  type UserProgressionStateModel,
} from "@/api/generated/models";
import { useCompleteOnboarding, useCompleteSignin } from "@/api/mutations";
import { useAttemptAffiliation } from "@/api/mutations/affiliate";
import { LoadingSpinner, Logo, Spacer, Text, YStack } from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { LayoutContext } from "@/components/LayoutProvider";
import AffiliateWelcomeScreen from "@/conversation/sidebar/AffiliateWelcomeScreen";
import UserProgressionFlow from "@/conversation/sidebar/UserProgression/UserProgressionFlow";
import { useAnalytics, useEventContext } from "@/hooks/analytics";
import { useIsSignedInStable } from "@/hooks/useIsSignedInStable";
import { usePartnerIntent } from "@/hooks/usePartnerIntent";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import {
  addBreadcrumb,
  captureException,
  captureMessage,
  setTag,
} from "@/sentry";
import { useAuthStore } from "@/store";
import { useActiveProgressionStore } from "@/store/activeProgression";
import { isMembershipPartner } from "@/utils/partner";
import { isAllowedRedirectUrl } from "@/utils/redirectUrl";
import { SIGN_IN_FLOW } from "./SignIn";

export interface LoadingProps {
  signInFlowRef: React.RefObject<SIGN_IN_FLOW>;
  conversationId?: string;
  onComplete?: () => void;
  isEmbedded?: boolean;
  /** Shown instead of the spinner, for hosts that have their own loading state. */
  loadingIndicator?: ReactNode;
}

export default function Loading({
  signInFlowRef,
  conversationId: conversationIdProp,
  onComplete,
  isEmbedded,
  loadingIndicator,
}: LoadingProps) {
  const router = usePropagatedRouter();
  const hasCompletedRef = useRef(false);

  const { track } = useAnalytics();
  const eventContext = useEventContext();
  const queryClient = useQueryClient();
  const { mutateAsync: completeSignin } = useCompleteSignin();
  const { mutateAsync: completeOnboarding } = useCompleteOnboarding();
  const { redirectUrl, currentConversationId } = useGlobalSearchParams<{
    redirectUrl: RelativePathString;
    currentConversationId: string;
  }>();
  const isSignedInStable = useIsSignedInStable();
  const { reset: resetAuthStore } = useAuthStore();
  const { getLocalPartnerIntent } = usePartnerIntent();
  const { setModalContent } = useContext(LayoutContext);
  const { value: partnerAffiliationEnabled } = useFeatureFlag(
    "partner-affiliation",
    false,
  );
  const { mutateAsync: attemptAffiliation } = useAttemptAffiliation();
  const activeProgression = useActiveProgressionStore(
    (s) => s.activeProgression,
  );

  // Use conversationId from prop, otherwise fall back to search params
  const conversationId = conversationIdProp ?? currentConversationId;

  useEffect(() => {
    const handleComplete = async () => {
      if (hasCompletedRef.current) return;
      hasCompletedRef.current = true;
      resetAuthStore();
      try {
        const partnerIntentModel = await getLocalPartnerIntent();
        setTag("partner_intent.present", Boolean(partnerIntentModel));
        setTag("partner_intent.partner", partnerIntentModel?.partner ?? "none");
        addBreadcrumb({
          category: "partner-intent",
          level: "info",
          message: `Registration handoff (${signInFlowRef.current}) — partner=${partnerIntentModel?.partner ?? "none"}, external_id=${partnerIntentModel?.external_id ?? "none"}`,
        });
        const requestData = {
          ...(conversationId && { conversation_id: conversationId }),
          ...(partnerIntentModel && { partner_intent: partnerIntentModel }),
        };

        if (signInFlowRef.current === "sign-up") {
          setTag("event.registered", true);
          // need to wait so conversation history is accurate
          await completeOnboarding({
            data: requestData,
          }).then(() => {
            track("superclinical-__completed-registration", {
              event_context: {
                ...eventContext,
                conversationId,
              },
            });
          });
        } else {
          await completeSignin({
            data: requestData,
          }).then(() => {
            track("superclinical-__completed-signin", {
              event_context: {
                ...eventContext,
                conversationId,
              },
            });
          });
        }

        const safeRedirect =
          redirectUrl && isAllowedRedirectUrl(redirectUrl) ? redirectUrl : "/";
        const isSignIn = signInFlowRef.current !== "sign-up";
        queryClient.invalidateQueries();

        // For users with partner intent, present the user an aggressive profile builder flow
        // just after registration to proactively encourage affiliation matching as early as
        // possible (which, for membership partners, requires a fully built out profile and ID
        // verification).
        // Skip when another UserProgression is already active — the caller may be a route
        // reached while a parent flow is still mounted in the sidebar, and we don't want to
        // clobber it. `onComplete` alone is not a sufficient signal because standalone
        // SidebarSignin entry points (AuthenticationBar, Nudge, AttachmentsButton) provide
        // an onComplete that just closes the sidebar — those paths SHOULD kick off
        // partner_post_registration on sign-up with partner intent.
        if (partnerAffiliationEnabled && !activeProgression) {
          if (
            !isSignIn &&
            partnerIntentModel &&
            isMembershipPartner(partnerIntentModel.partner)
          ) {
            let partnerProgression: UserProgressionStateModel | null = null;
            try {
              partnerProgression = await getOrCreateUserProgression({
                flow_name: "partner_post_registration",
                conversation_id: conversationId,
                metadata: { partner_intent: partnerIntentModel },
              });
            } catch (e) {
              captureMessage(`Failed to start partner_post_registration: ${e}`);
            }
            if (partnerProgression) {
              const progression = partnerProgression;
              setModalContent(() => (props: any) => (
                <UserProgressionFlow
                  initialProgression={progression}
                  {...props}
                />
              ));
              router.dismissTo(safeRedirect);
              return;
            }
          }

          // After sign-in we attempt to claim an affiliation so we can surface the
          // welcome screen for any newly-claimed membership affiliation. With no
          // partner intent, this is a broad-match attempt (any available membership
          // partner). Marketplace intents are handled server-side in complete_signin
          // and don't surface a welcome screen, so skip the redundant call.
          const shouldAttemptAffiliation =
            !partnerIntentModel ||
            isMembershipPartner(partnerIntentModel.partner);
          if (isSignIn && shouldAttemptAffiliation) {
            const { outcome, newly_affiliated_partner_uids } =
              await attemptAffiliation(partnerIntentModel ?? null);
            if (outcome === AffiliateOutcome.newly_created) {
              // We currently only welcome a single partner — the BE filters the
              // broad-match list to membership partners and there is only one
              // (WorkWhile). First uid wins until we support multi-partner.
              const partnerUid = newly_affiliated_partner_uids?.[0];
              setModalContent(() => () => (
                <AffiliateWelcomeScreen
                  onContinue={() => setModalContent(null)}
                  partnerUid={partnerUid}
                />
              ));
              return;
            }
          }
        }

        // Fallthrough: no partner-related sidebar mount happened. Defer to the caller's
        // onComplete (e.g. parent flow advance, AuthenticationBar close-sidebar) or the
        // default redirect.
        if (onComplete) {
          onComplete();
        } else {
          router.dismissTo(safeRedirect);
        }
      } catch (error) {
        captureException("Error completing auth flow", {
          extra: { flow: signInFlowRef.current, error },
        });
        queryClient.invalidateQueries();
        const safeRedirect =
          redirectUrl && isAllowedRedirectUrl(redirectUrl) ? redirectUrl : "/";
        if (onComplete) {
          onComplete();
        } else router.dismissTo(safeRedirect);
      }
    };

    if (isSignedInStable) {
      handleComplete();
    }
  }, [
    completeOnboarding,
    completeSignin,
    router,
    queryClient,
    redirectUrl,
    conversationId,
    isSignedInStable,
    signInFlowRef,
    resetAuthStore,
    eventContext,
    onComplete,
    track,
    getLocalPartnerIntent,
    setModalContent,
    partnerAffiliationEnabled,
    attemptAffiliation,
    activeProgression,
  ]);

  if (loadingIndicator) return loadingIndicator;

  return (
    <>
      {!isEmbedded && (
        <>
          <Logo marginTop="$1600" $lg={{ marginTop: "$600" }} />
          <Spacer size={150} $lg={{ size: 20 }} />
        </>
      )}
      <>
        <LoadingSpinner size={72} />
        <YStack marginTop="$100">
          <Text textAlign="center" bold>
            Please stand by
          </Text>
          <Text textAlign="center" color="$textSecondary">
            It'll only take a moment...
          </Text>
        </YStack>
      </>
    </>
  );
}
